import { useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Empty,
  List,
  Result,
  Space,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  CarOutlined,
  DollarOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import type { PaymentMethodSummary } from '@goelectric/shared-types';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/features/auth/context/AuthContext';
import { canWriteClientes } from '@/layouts/navigation';
import { AssignmentSection } from '../asignaciones/components/AssignmentSection';
import { canWriteAssignments } from '../asignaciones/utils/roles';
import { BalanceAdjustModal } from '../components/BalanceAdjustModal';
import { CustomerStatusBadge, CustomerTypeBadge } from '../components/CustomerBadges';
import { PendingMetric } from '../components/PendingMetric';
import { customerApi } from '../api/customerApi';
import { useCustomer } from '../hooks/useCustomer';
import { describeCustomerApiError } from '../utils/customerErrors';
import { PAYMENT_METHOD_TYPE_LABELS, formatCop, formatDateTime } from '../utils/format';

/** Nota de las secciones cuyo backend llega con historias posteriores (sin inventar datos). */
function PendingSection({ note }: { note: string }) {
  return (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={<Typography.Text type="secondary">{note}</Typography.Text>}
    />
  );
}

/** Método de pago tokenizado: solo referencia, marca y últimos 4 dígitos (PCI-DSS). */
function PaymentMethodRow({ method }: { method: PaymentMethodSummary }) {
  return (
    <List.Item>
      <Space wrap>
        <Typography.Text strong>{PAYMENT_METHOD_TYPE_LABELS[method.type]}</Typography.Text>
        {method.brand && <span>{method.brand}</span>}
        {method.last4 && <span style={{ fontFamily: 'var(--ge-font-mono)' }}>•••• {method.last4}</span>}
        {method.isDefault && <Tag color="blue">Predeterminado</Tag>}
        {method.tokenized && <Tag>Tokenizado</Tag>}
      </Space>
    </List.Item>
  );
}

/**
 * Detalle de Cliente (US-2.3, `/clientes/:customerId`).
 *
 * Muestra los datos de contacto, el estado de cuenta (saldo) y los métodos de pago
 * tokenizados. Permite ajustar el saldo (endpoint dedicado auditable) y suspender/
 * reactivar la cuenta con confirmación destructiva, todo solo con permiso de escritura.
 *
 * La sección “Packs y membresía” (US-2.4, TG-53) permite asignar/cancelar packs y
 * membresías y gestionar comprobantes (escritura solo `SUPER_ADMIN`); el historial de
 * sesiones (US-2.9) sigue como placeholder documentado. Los métodos de pago ya vienen
 * tokenizados por el backend.
 */
export function CustomerDetailPage() {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { message, modal } = App.useApp();
  const { customer, loading, error, notFound, refresh } = useCustomer(customerId);
  const canWrite = canWriteClientes(user?.role);
  const canWriteAsignaciones = canWriteAssignments(user?.role);
  const [balanceOpen, setBalanceOpen] = useState(false);
  const [statusSubmitting, setStatusSubmitting] = useState(false);

  const goToList = () => navigate('/clientes');

  /** Ejecuta un cambio de estado mostrando el mensaje real del backend y refrescando. */
  const changeStatus = async (
    action: () => Promise<unknown>,
    successMessage: string,
    fallbackMessage: string,
  ) => {
    if (!customerId) return;
    setStatusSubmitting(true);
    try {
      await action();
      message.success(successMessage);
      refresh();
    } catch (caught) {
      message.error(describeCustomerApiError(caught, fallbackMessage).message);
    } finally {
      setStatusSubmitting(false);
    }
  };

  /** Suspender es destructivo (cambia el estado de la cuenta): pide confirmación explícita. */
  const handleSuspend = () => {
    if (!customerId || !customer) return;
    modal.confirm({
      title: 'Suspender cliente',
      content: `${customer.fullName} quedará suspendido en el CRM. Este estado es administrativo y no bloquea la carga ni el saldo mientras no se integre con la autorización/OCPP. El historial se conserva y puedes reactivarlo cuando quieras.`,
      okText: 'Suspender',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: () =>
        changeStatus(
          () => customerApi.update(customerId, { status: 'SUSPENDED' }),
          'El cliente quedó suspendido en el CRM.',
          'No se pudo suspender el cliente.',
        ),
    });
  };

  const handleActivate = () => {
    if (!customerId) return;
    void changeStatus(
      () => customerApi.update(customerId, { status: 'ACTIVE' }),
      'El cliente quedó activo.',
      'No se pudo reactivar el cliente.',
    );
  };

  if (loading) {
    return <Card loading title="Cliente" />;
  }

  if (notFound) {
    return (
      <Result
        status="404"
        title="Cliente no encontrado"
        subTitle="El cliente no existe o fue eliminado."
        extra={<Button onClick={goToList}>Volver al listado</Button>}
      />
    );
  }

  if (error || !customer) {
    return (
      <Result
        status="error"
        title="No se pudo cargar el cliente"
        subTitle={error ?? undefined}
        extra={
          <Space>
            <Button type="primary" onClick={refresh}>
              Reintentar
            </Button>
            <Button onClick={goToList}>Volver al listado</Button>
          </Space>
        }
      />
    );
  }

  const account = customer.account;
  const paymentMethods = customer.paymentMethods ?? [];
  const fleetId = customer.fleetId;
  const isFleet = customer.tipoCliente === 'FLOTA';

  return (
    <div className="clientes-page">
      <div className="clientes-page__header">
        <Space align="center" wrap>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            aria-label="Volver al listado de clientes"
            onClick={goToList}
          />
          <Typography.Title level={3} style={{ margin: 0 }}>
            {customer.fullName}
          </Typography.Title>
          <CustomerStatusBadge status={customer.status} />
        </Space>
        {(canWrite || isFleet) && (
          <Space wrap>
            {canWrite && (
              <Button icon={<DollarOutlined />} onClick={() => setBalanceOpen(true)}>
                Ajustar saldo
              </Button>
            )}
            {isFleet ? (
              fleetId ? (
                <Button
                  icon={<CarOutlined />}
                  onClick={() => navigate(`/flotas/${encodeURIComponent(fleetId)}`)}
                >
                  Gestionar flota
                </Button>
              ) : (
                <Typography.Text type="secondary">
                  La suspensión se gestiona desde la ficha de la flota.
                </Typography.Text>
              )
            ) : customer.status === 'ACTIVE' ? (
              <Button
                danger
                icon={<PauseCircleOutlined />}
                loading={statusSubmitting}
                onClick={handleSuspend}
              >
                Suspender
              </Button>
            ) : (
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                loading={statusSubmitting}
                onClick={handleActivate}
              >
                Activar
              </Button>
            )}
          </Space>
        )}
      </div>

      <Card title="Datos del cliente">
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="ID">
            <span style={{ fontFamily: 'var(--ge-font-mono)' }}>{customer.id}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Tipo de cliente">
            <CustomerTypeBadge tipoCliente={customer.tipoCliente} />
          </Descriptions.Item>
          <Descriptions.Item label="Aliados con actividad">
            {customer.aliados && customer.aliados.length > 0 ? (
              <Space direction="vertical" size={4}>
                {customer.aliados.map((aliado) => (
                  <Space key={aliado.aliadoId} size={6} wrap>
                    <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                      {aliado.nombre}
                    </Tag>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {aliado.sessionsCount}{' '}
                      {aliado.sessionsCount === 1 ? 'sesión' : 'sesiones'} · Última:{' '}
                      {formatDateTime(aliado.lastSessionAt)}
                    </Typography.Text>
                  </Space>
                ))}
              </Space>
            ) : (
              '—'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Flota">
            {customer.fleetId ? (
              <Link to={`/flotas/${encodeURIComponent(customer.fleetId)}`}>
                {customer.fleetName ?? customer.fleetId}
              </Link>
            ) : (
              '—'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Teléfono">{customer.phone}</Descriptions.Item>
          <Descriptions.Item label="Email">{customer.email || '—'}</Descriptions.Item>
          <Descriptions.Item label="Sesiones">
            {customer.totalSessions !== undefined ? customer.totalSessions : <PendingMetric />}
          </Descriptions.Item>
          <Descriptions.Item label="Consumo">
            {customer.totalKwh !== undefined ? `${customer.totalKwh} kWh` : <PendingMetric />}
          </Descriptions.Item>
          <Descriptions.Item label="Creado">{formatDateTime(customer.createdAt)}</Descriptions.Item>
          <Descriptions.Item label="Actualizado">
            {formatDateTime(customer.updatedAt)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="Estado de cuenta">
        {account ? (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Saldo disponible">
              {formatCop(account.balanceCop)} {account.currency}
            </Descriptions.Item>
            <Descriptions.Item label="Sesiones totales">
              {account.totalSessionsCount !== undefined ? (
                account.totalSessionsCount
              ) : (
                <PendingMetric />
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Última sesión">
              {formatDateTime(account.lastSessionAt)}
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <Alert
            type="info"
            showIcon
            message="Sin estado de cuenta"
            description="El cliente todavía no tiene un estado de cuenta asociado."
          />
        )}
      </Card>

      {customerId && (
        <AssignmentSection
          customerId={customerId}
          customerName={customer.fullName}
          canWrite={canWriteAsignaciones}
          onChanged={refresh}
        />
      )}

      <Card title="Métodos de pago">
        {paymentMethods.length > 0 ? (
          <List<PaymentMethodSummary>
            dataSource={paymentMethods}
            renderItem={(method) => <PaymentMethodRow method={method} />}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No hay métodos de pago vinculados."
          />
        )}
      </Card>

      <Card title="Historial de sesiones">
        <PendingSection note="El historial de sesiones de carga (US-2.9) aún no está disponible. Aquí se verán las sesiones del cliente." />
      </Card>

      {customerId && (
        <BalanceAdjustModal
          open={balanceOpen}
          customerId={customerId}
          currentBalanceCop={account?.balanceCop ?? 0}
          onClose={() => setBalanceOpen(false)}
          onAdjusted={refresh}
        />
      )}
    </div>
  );
}
